import { Accessor, createMemo, createSignal, Setter } from 'solid-js';
import { CapabilityBase } from '@trimble/web3d-capabilities';
import {
  CapabilityProviderBase,
  SharedCapabilityProviderBase,
} from '@trimble/web3d-providers';
import { CapabilityCoordinatorBase } from '../capability-coordinator-base';
import {
  CapabilityBaseT,
  CapabilityMap,
  Constructor,
  ResourceExtractor,
} from './capability-registry-types';
import { CapabilitiesCollection } from './capabilities-collection';
import {
  isCapability,
  isCoordinator,
  isProvider,
  isSharedProvider,
} from './capability-registry-guards';

export abstract class CapabilityRegistryBase {
  private readonly registry: Accessor<CapabilityMap>;
  private readonly setRegistry: Setter<CapabilityMap>;

  protected constructor() {
    const [registry, setRegistry] = createSignal<CapabilityMap>(new Map());
    this.registry = registry;
    this.setRegistry = setRegistry;
  }

  protected getRegistry(): CapabilityMap {
    return this.registry();
  }

  private getExistingEntry<T extends CapabilityBaseT>(
    classRef: Constructor<T>
  ):
    | [
        Accessor<CapabilityBaseT | undefined>,
        Setter<CapabilityBaseT | undefined>,
      ]
    | undefined {
    const currentRegistry = this.registry();
    return currentRegistry.get(classRef as Constructor<CapabilityBaseT>);
  }

  public register<T extends CapabilityBaseT>(
    instanceClass: Constructor<T>,
    instance: T
  ): T {
    const currentRegistry = this.registry();
    const existing = this.getExistingEntry(instanceClass);

    if (existing) {
      const [, setter] = existing;
      setter(() => instance);
    } else {
      const [accessor, setter] = createSignal<T | undefined>(instance);
      const newRegistry = new Map(currentRegistry);
      newRegistry.set(instanceClass as Constructor<CapabilityBaseT>, [
        // eslint-disable-next-line solid/reactivity -- Storing accessor in registry, not using reactively
        accessor,
        setter as Setter<CapabilityBaseT | undefined>,
      ]);
      this.setRegistry(newRegistry);
    }

    return instance;
  }

  public get<T extends CapabilityBaseT>(
    classRef: Constructor<T>
  ): Accessor<T | undefined> {
    const currentRegistry = this.registry();
    const existing = this.getExistingEntry(classRef);

    if (existing) {
      return existing[0] as Accessor<T | undefined>;
    }

    const [accessor, setter] = createSignal<T | undefined>(undefined);
    const newRegistry = new Map(currentRegistry);

    newRegistry.set(classRef as Constructor<CapabilityBaseT>, [
      accessor as Accessor<CapabilityBaseT | undefined>,
      setter as Setter<CapabilityBaseT | undefined>,
    ]);
    this.setRegistry(newRegistry);

    return accessor;
  }

  public async unregister<T extends CapabilityBaseT>(
    instanceClass: Constructor<T>
  ): Promise<void> {
    const existing = this.getExistingEntry(instanceClass);

    if (existing) {
      const [accessor, setter] = existing;
      const instance = accessor();

      if (instance && 'cleanup' in instance) {
        await instance.cleanup();
      }

      setter(() => undefined);
    }
  }

  protected clearRegistry(): void {
    this.setRegistry(new Map());
  }

  private collectResources<T>(extractor: ResourceExtractor<T>): Accessor<T[]> {
    // eslint-disable-next-line solid/reactivity -- Returning memo directly, captured in calling context
    return createMemo((): T[] => {
      const results: T[] = [];
      const currentRegistry = this.registry();

      for (const [accessor] of currentRegistry.values()) {
        const resource = accessor();
        if (resource) {
          results.push(...extractor(resource));
        }
      }

      return results;
    });
  }

  protected collectCapabilities(): Accessor<CapabilityBase[]> {
    return this.collectResources((resource: CapabilityBaseT) => {
      if (resource instanceof CapabilitiesCollection) {
        return resource.capabilities;
      }
      return isCapability(resource) ? [resource] : [];
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected collectProviders(): Accessor<CapabilityProviderBase<any>[]> {
    return this.collectResources((resource: CapabilityBaseT) => {
      return isProvider(resource) ? [resource] : [];
    });
  }

  protected collectSharedProviders(): Accessor<
    SharedCapabilityProviderBase<CapabilityBase>[]
  > {
    return this.collectResources((resource: CapabilityBaseT) => {
      return isSharedProvider(resource) ? [resource] : [];
    });
  }

  protected collectCoordinators(): Accessor<CapabilityCoordinatorBase[]> {
    return this.collectResources((resource: CapabilityBaseT) => {
      return isCoordinator(resource) ? [resource] : [];
    });
  }
}
